import { OrderDocument } from '@models/order';
import { calculateEarnings } from '@util/firestore/orders';
import { getUsersByIds } from '@util/firestore/users';
import { UserDocument } from 'models/user';
import { getReturnsByOrderId } from './firestore/returns/returns.service';
import { ReturnState } from 'models/returns';

export const getEarningSummary = async (
  order: OrderDocument,
  userDoc?: UserDocument | null
) => {
  const variant = userDoc?.roles?.admin ? 'admin' : 'seller';
  let subtotal = 0;
  let shipping = 0;
  let insurance_total = 0;
  let total = 0;
  let earnings = 0;
  let tax = 0;
  let remittedTax = 0;
  let commission = 0;
  let refunded = 0;
  let processing = 0;
  let needsLabel: boolean = false;
  let shippingPaidByBuyer = 0;
  let seller_coupon = 0;

  if (order?.sellers && userDoc) {
    if (variant === 'seller') {
      const returnDocs = await getReturnsByOrderId(
        order.id,
        userDoc.uid,
        'seller_id'
      );
      order.sellers[userDoc.uid]?.forEach((item) => {
        subtotal += item.product_cost * item.qty;
        shipping += item.buyer_shipping_cost;
        if (item.rate_id) shippingPaidByBuyer += item.buyer_shipping_cost;
        const [n, c, t, p, i, remit, sc] = calculateEarnings({
          item,
          item_count: order.sellers[userDoc.uid].length,
          buyerState: order.address.state_province ?? '',
          sellerTaxStates: userDoc.tax_states,
          fee: userDoc.fee,
          created: order.created,
          coupon: order.coupon,
          returnDoc: returnDocs.find(
            (r) =>
              r.product_id === item.id && r.state === ReturnState.ReturnComplete
          ),
        });
        processing += p;
        earnings += n;
        commission += c;
        tax += t;
        remittedTax += remit;
        refunded += item.refund_amount ?? 0;
        insurance_total += i ?? 0;
        seller_coupon += sc ?? 0;
      });
      total = subtotal + shipping + tax + -refunded;
    } else if (userDoc?.roles?.admin) {
      const sellers = Object.keys(order.sellers);
      const sellerDocs = await getUsersByIds(sellers);
      const allReturnDocs = await Promise.all(
        sellers.map((seller) =>
          getReturnsByOrderId(order.id, seller, 'seller_id')
        )
      );
      const allReturnDocsFlat = allReturnDocs.flat();
      sellerDocs.forEach((seller) => {
        if (!seller) return;
        order.sellers?.[seller?.uid]?.forEach((item) => {
          const [n, c, t, p, i, remit, sc] = calculateEarnings({
            item,
            item_count: order.sellers[seller?.uid].length,
            buyerState: order.address.state_province ?? '',
            sellerTaxStates: seller?.tax_states,
            fee: seller?.fee,
            coupon: order.coupon,
            created: order.created,
            returnDoc: allReturnDocsFlat.find(
              (r) =>
                r.seller_id === seller.uid &&
                r.state === ReturnState.ReturnComplete &&
                r.product_id === item.id
            ),
          });
          earnings += n;
          commission += c;
          processing += p;
          tax += t;
          remittedTax += remit;
          insurance_total += i ?? 0;
          seller_coupon += sc ?? 0;
          refunded += item.refund_amount ?? 0;
          if (!!item.rate_id && !item.label_pdf) needsLabel = true;
          if (!item.rate_id) shipping += item.buyer_shipping_cost;
          else shippingPaidByBuyer += item.buyer_shipping_cost;
        });
      });
      if (shippingPaidByBuyer) shipping += shippingPaidByBuyer;
      subtotal = order.subtotal;
      total = order.total - refunded;
    } else {
      subtotal = order.subtotal;
      shipping = order.shipping_total ?? 0;
      tax = order.tax ?? 0;
      refunded += getRefundAmount(order);
      total = order.total;
    }
  }

  function getRefundAmount(order: OrderDocument) {
    let refund = 0;
    const sellers = Object.keys(order.sellers ?? {});
    sellers.forEach((seller) => {
      order.sellers?.[seller]?.forEach((item) => {
        refund += item.refund_amount ?? 0;
      });
    });
    return refund;
  }

  return {
    subtotal,
    shipping,
    insurance_total,
    total,
    earnings,
    tax,
    remittedTax,
    commission,
    refunded,
    needsLabel,
    processing,
    shippingPaidByBuyer,
    seller_coupon,
  };
};
